/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid,jsx-a11y/img-redundant-alt */
import React, { useEffect, useRef, useState } from 'react';
/* eslint-disable */
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { DragCircleMode } from 'mapbox-gl-draw-circle';
import StaticMode from '@mapbox/mapbox-gl-draw-static-mode';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { MapboxGLButtonControl } from '../../utils/MapboxGlButtons';
import { editStyles, readOnlyStyles } from './mapProps';
import { area } from '@turf/turf';

const mapTypes = {
  street: 'mapbox://styles/mapbox/streets-v11',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v11',
};

export function Map({
  formik,
  mapRef,
  setController,
  mapHeight,
  geofences,
  mapStyle,
  children,
}) {
  const mapContainer = useRef(null);
  const mapController = useRef(null);
  const [displayChildren, setDisplayChildren] = useState(true);
  const [size, setSize] = useState(null);

  const styles = {
    height: mapHeight,
    // position: "absolute"
  };

  useEffect(() => {
    if (mapController.current) {
      const resizer = new ResizeObserver(() => mapController.current.resize());
      resizer.observe(mapContainer.current);

      return () => {
        resizer.disconnect();
      };
    }
  }, [mapController.current]);

  const draw = new MapboxDraw({
    modes: {
      ...MapboxDraw.modes,
      drag_circle: DragCircleMode,
      static: StaticMode,
    },
    userProperties: true,
    displayControlsDefault: false,
    styles: mapStyle ? mapStyle : formik ? editStyles : readOnlyStyles,
  });

  draw.calculateArea = geofence => {
    const roundedArea = Math.round(area(geofence.geometry));
    setSize(roundedArea);
  };

  draw.clear = deleteAll;

  function deleteAll() {
    formik.setFieldValue('geoJson', '');
    setSize(null);
    draw.deleteAll();
  }

  function changeToCircle() {
    deleteAll();
    mapContainer.current.classList.add('mouse-add');
    draw.changeMode('drag_circle');
  }

  function changeToPolygon() {
    deleteAll();
    draw.changeMode('draw_polygon');
  }

  function changeMapStyle() {
    const currentStyle = mapController.current.getStyle().sprite;
    mapController.current.setStyle(
      currentStyle.includes('satellite') ? mapTypes.street : mapTypes.satellite
    );
  }

  useEffect(() => {
    let map;
    mapboxgl.accessToken =
      'pk.eyJ1IjoidXNlYXVyZWEiLCJhIjoiY2twZ3pwbzdnMHd0MDJ1cGYwMHhuanJoNSJ9.RsaMklzawAcvQgd6T-GfuQ';
    const initializeMap = () => {
      map = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapTypes.satellite,
        zoom: 2,
      });
      mapController.current = map;
      map.on('load', () => {
        map.addControl(new mapboxgl.NavigationControl());

        map.addControl(draw, 'top-left');

        if (formik) {
          map.addControl(
            new MapboxGLButtonControl({
              className: 'mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_polygon',
              eventHandler: changeToPolygon,
            }),
            'top-left'
          );

          map.addControl(
            new MapboxGLButtonControl({
              className: 'mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_trash',
              eventHandler: deleteAll,
            }),
            'top-left'
          );

          map.addControl(
            new MapboxGLButtonControl({
              className: 'mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_circle',
              eventHandler: changeToCircle,
            }),
            'top-left'
          );
        }

        map.addControl(
          new MapboxGLButtonControl({
            className: 'mapbox-gl-draw_ctrl-draw-btn mapbox-gl-change_type',
            eventHandler: changeMapStyle,
            position: 1,
          }),
          'top-right'
        );

        mapRef.current = map;
        setController(draw);

        map.resize();
      });

      map.on('draw.create', e => {
        formik.setFieldValue('geoJson', e.features[0]);
        mapContainer.current.classList.remove('mouse-add');
        const roundedArea = Math.round(area(draw.getAll()) * 100) / 100;
        setSize(roundedArea);
      });

      map.on('draw.update', e => {
        formik.setFieldValue('geoJson', e.features[0]);
        const roundedArea = Math.round(area(draw.getAll()) * 100) / 100;
        setSize(roundedArea);
      });

      map.on('draw.delete', () => {
        deleteAll();
      });
    };
    initializeMap();
    return () => {
      map.remove();
    };
  }, []);

  useEffect(() => {
    if (!formik && mapController.current) {
      mapController.current.on('load', () => {
        const coords = [];
        geofences.forEach(geo => {
          const coordinate = JSON.parse(geo.areaCentroid).coordinates;
          coords.push(coordinate);
          const popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
          });
          if (mapController.current.getSource(geo.name)) {
            mapController.current.removeLayer(geo.name);
            mapController.current.removeSource(geo.name);
          }
          mapController.current.addSource(geo.name, {
            type: 'geojson',
            data: geo.geoJson,
          });
          mapController.current.addLayer({
            id: geo.name,
            type: 'fill',
            source: geo.name,
            paint: {
              'fill-color': '#f08',
              'fill-opacity': 0.4,
            },
          });
          mapController.current.on('mouseenter', geo.name, e => {
            mapController.current.getCanvas().style.cursor = 'pointer';
            popup
              .setLngLat(coordinate)
              .setHTML(
                `
                            <h5 class='font-weight-bolder'>${geo.name}</h5>
                            <span>${geo.description}</span>
                        `
              )
              .addTo(mapController.current);
          });

          mapController.current.on('mouseleave', geo.name, () => {
            mapController.current.getCanvas().style.cursor = '';
            popup.remove();
          });
        });
        if (coords.length) {
          const bounds = new mapboxgl.LngLatBounds();

          coords.forEach(feature => {
            bounds.extend(feature);
          });
          mapController.current.fitBounds(bounds);
        }
      });
    }
  }, [geofences, mapController]);

  return (
    <div
      ref={mapContainer}
      style={styles}
      onFocus={() => setDisplayChildren(false)}
      onBlur={() => setDisplayChildren(true)}
    >
      {size ? (
        <div className="calculation-box d-flex justify-content-center align-items-center">
          {' '}
          {size} square meters{' '}
        </div>
      ) : null}
      {formik && displayChildren ? children : null}
    </div>
  );
}
